import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { AlertTriangle, Check, GraduationCap, Plus, Upload, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { get, ApiError } from '@/lib/api';
import type { DecorationRegistry, PrizeProduct } from '@/lib/prize-product';
import {
  MAX_PRIZE_TIERS, prizeIndexes, canAddPrizeProduct, assertTierCap,
} from '@/lib/prize-tiers';
import {
  slotOptions, tierDecorationView, zoneGroups, zoneStackPair,
  blockedCodes, slotConflictPairs, slotConflictMessage, zoneTextSpec,
  appendTierDecoration, slotMissingFile, slotsFitProduct,
  addZone, addStackPair, removeSlot, setSlotCode, setSlotKind, setSlotText,
  setSlotCaptionStyle, setSlotFont, setSlotInk, slotTypographyDefaults,
  ADDTEXT_PICKER_FONTS,
  SLOT_KIND_LABEL, SLOT_FILE_ACCEPT,
  type IntakeSlot, type SlotFiles, type ZoneOption,
} from '@/lib/submission-zones';
import {
  PRIZE_FONTS, isTextFillKind, resolvePrizeFont, resolvePrizeSlotInk,
} from '@/lib/prize-product';
import { CaptionColorField } from './CaptionColorField';
import { PrizeInkField } from './PrizeInkField';
import { AddtextFontSelect } from './AddtextFontSelect';

// New Prize Submission — operator-side version of the public/rep submission
// form. Relays through POST /api/submissions/create → submitlogo (the
// canonical writer), so id generation, PNG conversion, and the preflight
// pipeline behave exactly like every other submission.
//
// ZONE-FIRST INTAKE: pick the garment, then its print ZONES, then what each zone
// prints (logo / player name / player number). All of it is registry-driven —
// see ../../lib/submission-zones.ts, which owns every rule and is where they are
// tested. This component only renders what that module decides.
//
// A LEGACY product (no printAreas) renders exactly today's single-logo form and
// POSTs the pre-slot-mode contract field for field: no decoration fields go on
// the wire at all, so submitlogo takes its legacy path.

type School = {
  placeId?: string;
  ghostfit_id?: string;
  name?: string;
  school_name?: string;
  city?: string;
  state?: string;
  primary_color?: string;
};

type Admin = { id: string; name?: string; email?: string };

type SportsPayload = { sports?: Record<string, unknown> | unknown[] } | unknown[];

function sportNames(data?: SportsPayload): string[] {
  const raw = Array.isArray(data) ? data : data?.sports;
  if (Array.isArray(raw)) {
    return raw.map((s) => (typeof s === 'string' ? s : (s as { name?: string; sport?: string })?.name || (s as { sport?: string })?.sport || '')).filter(Boolean);
  }
  if (raw && typeof raw === 'object') return Object.keys(raw);
  return [];
}

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || fallback;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (submissionId: string) => void;
};

export function NewSubmissionDialog({ open, onOpenChange, onCreated }: Props) {
  // School lookup (optional but recommended — links scout + auto-names)
  const [schoolQuery, setSchoolQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [school, setSchool] = useState<School | null>(null);
  const [sport, setSport] = useState('');
  const [fundraiserName, setFundraiserName] = useState('');
  const [admin, setAdmin] = useState('');
  const [productCount, setProductCount] = useState(1);
  const [tierProduct, setTierProduct] = useState<Record<number, string>>({ 1: '' });
  const [useSameLogo, setUseSameLogo] = useState<Record<number, boolean>>({});
  const [underText, setUnderText] = useState('');
  const [underTextFont, setUnderTextFont] = useState<string>('zingrustbase');
  const [underTextColor, setUnderTextColor] = useState<string>('auto');
  const [tierFile, setTierFile] = useState<Record<number, File | null>>({ 1: null });
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef<Record<number, HTMLInputElement | null>>({});
  // Per-product decoration slots. `null` = this product is not in zone mode
  // (legacy product, or nothing picked yet) and contributes NO decoration fields.
  const [slots, setSlots] = useState<Record<number, IntakeSlot[] | null>>({ 1: null });
  const [slotFiles, setSlotFiles] = useState<Record<number, SlotFiles>>({ 1: {} });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(schoolQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [schoolQuery]);

  useEffect(() => {
    if (open) {
      setSchoolQuery(''); setSchool(null); setSport(''); setFundraiserName('');
      setProductCount(1);
      setTierProduct({ 1: '' });
      setUseSameLogo({});
      setUnderText('');
      setUnderTextFont('zingrustbase');
      setUnderTextColor('auto');
      setTierFile({ 1: null });
      setSlots({ 1: null }); setSlotFiles({ 1: {} });
    }
  }, [open]);

  const { data: schoolResults, isLoading: searching } = useSWR<{ schools: School[] }>(
    open && debounced.length >= 2 && !school ? `/api/scout/schools?search=${encodeURIComponent(debounced)}&limit=10` : null,
    get
  );
  const schoolId = school?.ghostfit_id || school?.placeId;
  const { data: sportsData } = useSWR<SportsPayload>(
    open && schoolId ? `/api/scout/schools/${encodeURIComponent(String(schoolId))}/sports` : null,
    get,
    { shouldRetryOnError: false }
  );
  const sports = useMemo(() => sportNames(sportsData), [sportsData]);

  const { data: productsData } = useSWR<{ products: PrizeProduct[] }>(open ? '/api/prize-products' : null, get);
  const products = (productsData?.products || [])
    .filter((p) => p.active !== false)
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || String(a.displayName || a.id).localeCompare(String(b.displayName || b.id)));

  const { data: adminsData } = useSWR<{ admins: Admin[] }>(open ? '/api/configuration' : null, get);
  const admins = (adminsData?.admins || []).filter((a) => a.name);

  // platformConfig/decorationLocations. The server route (routes/proxy.js) reads
  // it through the bounded-staleness loader and 503s rather than serving a
  // fallback map, so an error here means the zone list genuinely cannot be
  // trusted — see the fail-closed rule in tierDecorationView.
  const { data: registry, error: registryFetchError, isLoading: registryLoading } =
    useSWR<DecorationRegistry>(open ? '/api/decoration-locations' : null, get, { shouldRetryOnError: false });
  const registryError = registryFetchError
    ? errMsg(registryFetchError, 'decoration locations unavailable')
    : null;

  function pickSchool(s: School) {
    setSchool(s);
    setSchoolQuery(s.name || s.school_name || '');
    if (!fundraiserName) setFundraiserName(`${s.name || s.school_name || ''}${sport ? ` ${sport}` : ''}`.trim());
  }

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const activeIndexes = useMemo(() => prizeIndexes(productCount), [productCount]);

  function addProduct() {
    if (!canAddPrizeProduct(productCount)) return;
    const n = productCount + 1;
    setProductCount(n);
    setTierProduct((p) => ({ ...p, [n]: '' }));
    setUseSameLogo((s) => ({ ...s, [n]: n === 2 }));
    setTierFile((f) => ({ ...f, [n]: null }));
    setSlots((s) => ({ ...s, [n]: null }));
    setSlotFiles((f) => ({ ...f, [n]: {} }));
  }

  function removeLastProduct() {
    if (productCount <= 1) return;
    const n = productCount;
    setProductCount(n - 1);
    setTierProduct((p) => { const next = { ...p }; delete next[n]; return next; });
    setUseSameLogo((s) => { const next = { ...s }; delete next[n]; return next; });
    setTierFile((f) => { const next = { ...f }; delete next[n]; return next; });
    setSlots((s) => { const next = { ...s }; delete next[n]; return next; });
    setSlotFiles((f) => { const next = { ...f }; delete next[n]; return next; });
  }

  // Everything the decoration section of a product needs, derived fresh from the
  // registry + the selected product. Never cached into state: state holds only
  // the operator's CHOICES (slots + files), so a product change can never leave
  // a zone list describing the previous garment.
  const tierViews = useMemo(() => {
    const out: Record<number, { product: PrizeProduct | null; opts: ZoneOption[]; view: ReturnType<typeof tierDecorationView> }> = {};
    for (const n of activeIndexes) {
      const product = productById.get(tierProduct[n] || '') || null;
      const opts = registryLoading ? [] : slotOptions(product, registry);
      const view = registryLoading && product
        ? ({ mode: 'legacy' } as ReturnType<typeof tierDecorationView>)
        : tierDecorationView(product, opts, registryError);
      out[n] = { product, opts, view };
    }
    return out;
  }, [productById, tierProduct, activeIndexes, registry, registryError, registryLoading]);

  // Seed / tear down each product's slot list as its garment changes. Slot 0 is
  // the primary logo by contract; a product that is not in zone mode is reset to
  // null so it submits the legacy contract with no decoration fields at all.
  useEffect(() => {
    for (const n of activeIndexes) {
      const tv = tierViews[n];
      if (!tv) continue;
      const { view, opts } = tv;
      const current = slots[n];
      if (view.mode === 'zones') {
        if (!slotsFitProduct(opts, current)) {
          setSlots((s) => ({ ...s, [n]: [{ code: view.primary, kind: 'logo' }] }));
          setSlotFiles((f) => ({ ...f, [n]: {} }));
        }
      } else if (current) {
        setSlots((s) => ({ ...s, [n]: null }));
        setSlotFiles((f) => ({ ...f, [n]: {} }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierViews]);

  const blockingError = activeIndexes
    .map((n) => tierViews[n]?.view)
    .find((v) => v?.mode === 'error');

  const slotProblem = (() => {
    for (const n of activeIndexes) {
      const list = slots[n];
      if (!list?.length) continue;
      const conflicts = slotConflictPairs(registry, list);
      if (conflicts.length) return `Product ${n}: ${slotConflictMessage(registry, conflicts[0])}`;
      const missing = slotMissingFile(list, slotFiles[n] || {});
      if (missing) return `Product ${n} print zone ${missing.code} needs a logo file.`;
    }
    return null;
  })();

  const productsPicked = activeIndexes.every((n) => !!tierProduct[n]);
  const logosPicked = activeIndexes.every((n) => {
    if (n === 1) return !!tierFile[1];
    if (useSameLogo[n]) return true;
    return !!tierFile[n];
  });
  const valid = productsPicked && !!admin && logosPicked && !blockingError && !slotProblem;

  async function submit() {
    if (!valid || !tierFile[1]) return;
    try {
      assertTierCap(activeIndexes);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Maximum 8 products.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (fundraiserName.trim()) fd.append('fundraiserName', fundraiserName.trim());
      fd.append('adminSelect', admin);
      for (const n of activeIndexes) {
        fd.append(`tier${n}Product`, tierProduct[n]);
        if (n > 1) fd.append(`tier${n}UseSameLogo`, useSameLogo[n] ? 'true' : 'false');
      }
      let slotState = slots;
      const t1Slots = slotState[1];
      const t1Primary = Array.isArray(t1Slots) && t1Slots[0] && t1Slots[0].kind === 'logo' ? t1Slots[0] : null;
      if (Array.isArray(t1Slots) && t1Primary && !t1Primary.decoration?.text?.trim() && underText.trim()) {
        const next = setSlotText(t1Slots, 0, underText.trim(), { font: underTextFont, textColor: underTextColor });
        slotState = { ...slotState, 1: next };
      }
      const deco = slotState[1]?.[0]?.kind === 'logo' ? slotState[1][0].decoration : null;
      const captionText = (deco?.text && deco.text.trim()) || underText.trim();
      if (captionText) {
        fd.append('underText', captionText);
        fd.append('underTextFont', deco?.font || underTextFont);
        fd.append('underTextColor', deco?.textColor || underTextColor);
      }
      if (school) {
        if (school.ghostfit_id) fd.append('ghostfit_id', school.ghostfit_id);
        fd.append('school_name', school.name || school.school_name || '');
      }
      if (sport.trim()) {
        fd.append('sportType', sport.trim());
        if (sports.includes(sport.trim())) fd.append('sport_id', sport.trim());
      }
      for (const n of activeIndexes) {
        const file = tierFile[n];
        if (n > 1 && useSameLogo[n]) continue;
        if (file) fd.append(`tier${n}LogoFile`, file);
      }
      for (const n of activeIndexes) {
        appendTierDecoration(fd, n, slotState[n], slotFiles[n] || {});
      }

      const res = await fetch('/api/submissions/create', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body);
      toast.success(`Submission created — ${body.submissionId}. Preflight runs automatically.`);
      await mutate((k: unknown) => typeof k === 'string' && k.startsWith('/api/submissions'), undefined, { revalidate: true });
      onOpenChange(false);
      onCreated(body.submissionId);
    } catch (e) {
      toast.error(errMsg(e, 'Submission failed'));
    } finally {
      setBusy(false);
    }
  }

  const selCls = 'h-8 w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15';
  const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-h-[88vh] max-w-[560px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Prize Submission</DialogTitle>
          <p className="text-[11.5px] text-muted">
            Creates the submission and uploads the logo — preflight and the proof pipeline take it from there.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* School */}
          <div>
            <label className={labelCls}>School (links scout record)</label>
            <div className="relative">
              <Input
                value={schoolQuery}
                onChange={(e) => { setSchoolQuery(e.target.value); setSchool(null); }}
                placeholder="Search schools…"
                className="pl-8"
              />
              <GraduationCap className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
            </div>
            {!school && debounced.length >= 2 && (
              <div className="mt-1 max-h-[160px] overflow-y-auto rounded-[10px] border border-black/[0.06] bg-surface shadow-raised">
                {searching ? (
                  <div className="space-y-1.5 p-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
                ) : (schoolResults?.schools || []).length === 0 ? (
                  <p className="p-2.5 text-[11px] text-muted">No matches — you can submit without a school link.</p>
                ) : (
                  (schoolResults?.schools || []).map((s) => (
                    <button
                      key={String(s.placeId || s.ghostfit_id)}
                      onClick={() => pickSchool(s)}
                      className="flex w-full items-center gap-2 border-b border-divider px-2.5 py-1.5 text-left transition-colors last:border-b-0 hover:bg-bg/70"
                    >
                      <span className="flex-1 truncate text-[12px]">{s.name || s.school_name}</span>
                      <span className="text-[10.5px] text-subtle">{[s.city, s.state].filter(Boolean).join(', ')}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            {school && (
              <p className="mt-1 flex items-center gap-1.5 text-[11px] text-primary">
                <Check className="h-3.5 w-3.5" aria-hidden /> {school.name || school.school_name}
                <button className="text-muted underline-offset-2 hover:underline" onClick={() => { setSchool(null); setSchoolQuery(''); }}>change</button>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Sport</label>
              {sports.length > 0 && (
                <div className="mb-1.5 flex flex-wrap gap-1">
                  {sports.slice(0, 6).map((s) => (
                    <button
                      key={s}
                      onClick={() => { setSport(s); if (school) setFundraiserName(`${school.name || school.school_name} ${s}`); }}
                      className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium capitalize transition-colors ${sport === s ? 'bg-text text-white' : 'bg-black/[0.05] text-muted hover:bg-black/[0.08]'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="football" />
            </div>
            <div>
              <label className={labelCls}>Fundraiser name</label>
              <Input value={fundraiserName} onChange={(e) => setFundraiserName(e.target.value)} placeholder="Lincoln Football" maxLength={140} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Admin *</label>
            <select className={selCls} value={admin} onChange={(e) => setAdmin(e.target.value)}>
              <option value="">Select admin…</option>
              {admins.map((a) => <option key={a.id} value={a.name}>{a.name}{a.email ? ` (${a.email})` : ''}</option>)}
            </select>
          </div>

          {/* Products — 1..N, cap 8. Empty T2 is not rendered. Every active
              garment is pickable in any slot; catalog prizeProducts.tiers is
              eligibility for the Prize Products editor, not this form. */}
          <div className="space-y-3">
            {activeIndexes.map((n) => (
              <div key={n} className="rounded-[10px] border border-black/[0.06] bg-bg/40 p-2.5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className={labelCls} style={{ marginBottom: 0 }}>
                    Product {n}{n === 1 ? ' *' : ''}
                  </label>
                  {n === productCount && n > 1 && (
                    <button type="button" className="text-[10px] font-medium text-primary" onClick={removeLastProduct}>
                      Remove
                    </button>
                  )}
                </div>
                <select
                  className={selCls}
                  value={tierProduct[n] || ''}
                  onChange={(e) => setTierProduct((p) => ({ ...p, [n]: e.target.value }))}
                >
                  <option value="">Select…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.displayName || p.id}</option>)}
                </select>
                <div className="mt-2">
                  <label className={labelCls}>Logo{n === 1 ? ' *' : ''}</label>
                  {n > 1 && (
                    <label className="mb-1.5 flex items-center gap-1.5 text-[11.5px]">
                      <input
                        type="checkbox"
                        className="accent-[#007aff]"
                        checked={!!useSameLogo[n]}
                        onChange={(e) => setUseSameLogo((s) => ({ ...s, [n]: e.target.checked }))}
                      />
                      Use the product 1 logo
                    </label>
                  )}
                  {(n === 1 || !useSameLogo[n]) && (
                    <>
                      <input
                        ref={(el) => { fileRefs.current[n] = el; }}
                        type="file"
                        accept="image/*,.pdf,.ai,.eps"
                        className="hidden"
                        onChange={(e) => setTierFile((f) => ({ ...f, [n]: e.target.files?.[0] || null }))}
                      />
                      <Button variant="secondary" className="w-full" onClick={() => fileRefs.current[n]?.click()}>
                        <Upload className="h-3.5 w-3.5" aria-hidden /> {tierFile[n] ? tierFile[n]!.name.slice(0, 22) : 'Choose file'}
                      </Button>
                      {n === 1 && <p className="mt-1 text-[10px] text-subtle">PNG preferred — PDF/AI/EPS are converted.</p>}
                    </>
                  )}
                </div>
                {tierViews[n] && (
                  <div className="mt-2">
                    <TierZones
                      tierN={n}
                      inheritPrimaryCaption={n > 1 && !!useSameLogo[n]}
                      opts={tierViews[n].opts}
                      view={tierViews[n].view}
                      registry={registry}
                      slots={slots[n]}
                      files={slotFiles[n] || {}}
                      onSlots={(next, nextFiles) => {
                        setSlots((s) => ({ ...s, [n]: next }));
                        if (nextFiles) setSlotFiles((f) => ({ ...f, [n]: nextFiles }));
                      }}
                      onFile={(index, file) => setSlotFiles((f) => ({ ...f, [n]: { ...(f[n] || {}), [index]: file } }))}
                      onClearFile={(index) => setSlotFiles((f) => {
                        const next = { ...(f[n] || {}) };
                        delete next[index];
                        return { ...f, [n]: next };
                      })}
                    />
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!canAddPrizeProduct(productCount)}
                onClick={addProduct}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add product
              </Button>
              <span className="text-[11px] text-muted">
                {productCount >= MAX_PRIZE_TIERS
                  ? 'Maximum 8 products'
                  : (productCount === 1 ? 'One product — add up to 8' : `${productCount} of 8 products`)}
              </span>
            </div>
          </div>

          <div>
            <label className={labelCls}>Text under logo (optional)</label>
            <p className="mb-1 text-[10px] text-subtle">Fills Product 1&apos;s primary logo when that slot has no caption of its own. Each placement (Left Chest, Full Front, …) has its own text, font, and color next to the zone.</p>
            <Input value={underText} onChange={(e) => setUnderText(e.target.value)} placeholder="e.g. LINCOLN FOOTBALL" maxLength={100} />
            {underText.trim() ? (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label>
                  <span className="mb-0.5 block text-[10px] uppercase tracking-[0.04em] text-muted">Font</span>
                  <AddtextFontSelect value={underTextFont} onChange={setUnderTextFont} fonts={[...ADDTEXT_PICKER_FONTS]} />
                </label>
                <label>
                  <span className="mb-0.5 block text-[10px] uppercase tracking-[0.04em] text-muted">Text color</span>
                  <CaptionColorField value={underTextColor} onChange={setUnderTextColor} />
                </label>
              </div>
            ) : null}
          </div>
        </div>

        {slotProblem && (
          <p className="flex items-start gap-1.5 rounded-[8px] bg-red-500/10 px-2.5 py-2 text-[11.5px] text-red-600">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> {slotProblem}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-divider pt-3">
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || busy} onClick={submit}>
            {busy ? 'Creating…' : 'Create submission'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// One tier's "Print zones" section.
//
// Dumb by design: every decision — which zones are offerable, which are blocked
// by a conflict, whether a stacked pair exists, what a zone may print — comes
// from ../../lib/submission-zones.ts. Nothing here knows a location code.
// ---------------------------------------------------------------------------

type TierZonesProps = {
  tierN: number;
  inheritPrimaryCaption?: boolean;
  opts: ZoneOption[];
  view: ReturnType<typeof tierDecorationView>;
  registry: DecorationRegistry | undefined;
  slots: IntakeSlot[] | null;
  files: SlotFiles;
  onSlots: (next: IntakeSlot[], nextFiles?: SlotFiles) => void;
  onFile: (index: number, file: File) => void;
  onClearFile: (index: number) => void;
};

function TierZones({ tierN, inheritPrimaryCaption, opts, view, registry, slots, files, onSlots, onFile, onClearFile }: TierZonesProps) {
  const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted';
  const selCls = 'h-8 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15';

  // FAIL CLOSED. A product with configured print zones whose registry could not
  // be read is blocked outright — never downgraded to a legacy submission.
  if (view.mode === 'error') {
    return (
      <div className="rounded-[10px] border border-red-500/30 bg-red-500/[0.06] p-2.5">
        <p className="flex items-start gap-1.5 text-[11.5px] text-red-600">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span><strong>Product {tierN} print zones unavailable.</strong> {view.error}</span>
        </p>
      </div>
    );
  }
  // A legacy product has one derived print area: today's single-logo form,
  // unchanged, with no zone picker and nothing extra on the wire.
  if (view.mode !== 'zones' || !slots?.length) return null;

  const taken = new Set(slots.map((s) => s.code));
  const conflicts = slotConflictPairs(registry, slots);
  const groups = zoneGroups(registry, opts, slots);
  const pair = zoneStackPair(registry, opts, slots);
  const nothingLeft = !groups.standard.length && !groups.other.length && !pair;

  const chip = (o: ZoneOption) => (
    <button
      key={o.code}
      type="button"
      onClick={() => onSlots(addZone(registry, opts, slots, o.code))}
      className="rounded-full border border-black/[0.08] bg-surface px-2.5 py-1 text-[11px] transition-colors hover:bg-bg"
    >
      + {o.label}
      <span className="ml-1.5 text-subtle">{o.code}{o.size ? ` · ${o.size}` : ''}</span>
    </button>
  );

  return (
    <div className="rounded-[10px] border border-black/[0.06] bg-bg/40 p-2.5">
      <label className={labelCls}>Product {tierN} print zones</label>
      <p className="mb-2 text-[11px] text-muted">
        Where this prize decorates. The first zone is the primary — it prints the Product {tierN} artwork chosen above.
      </p>

      {slots.map((slot, i) => {
        const opt = opts.find((o) => o.code === slot.code) || opts[0];
        const kinds = opt ? opt.kinds : (['logo'] as IntakeSlot['kind'][]);
        // A code excluded by ANOTHER row is dropped from this row's dropdown.
        // The row's OWN code always stays listed, even when it conflicts — hiding
        // it would leave the select showing something never picked, and the
        // inline error below already says what to change.
        const blocked = blockedCodes(registry, slots, i);
        const rowOpts = opts
          .filter((o) => o.code === slot.code || (!taken.has(o.code) && !blocked.has(o.code)))
          // Slot 0 must be a logo — submitlogo mirrors it to the legacy tier
          // logo fields and rejects any other kind there.
          .filter((o) => (i === 0 ? o.kinds.includes('logo') : true));
        const hint = [opt?.size || '', zoneTextSpec(opt, slot.kind)].filter(Boolean).join(' · ');
        const file = files[i];

        return (
          <div key={i} className="mb-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <select
                className={`${selCls} min-w-0 flex-1`}
                value={slot.code}
                onChange={(e) => {
                  const out = setSlotCode(opts, slots, files, i, e.target.value);
                  onSlots(out.slots, out.files);
                }}
              >
                {rowOpts.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>

              {i === 0 ? (
                <span className="flex-1 text-[11px] text-muted">
                  <span className="mr-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">primary</span>
                  Logo — uses the artwork above
                </span>
              ) : (
                <select
                  className={`${selCls} w-[130px]`}
                  value={slot.kind}
                  onChange={(e) => {
                    const out = setSlotKind(slots, files, i, e.target.value as IntakeSlot['kind'], opt?.area);
                    onSlots(out.slots, out.files);
                  }}
                >
                  {kinds.map((k) => <option key={k} value={k}>{SLOT_KIND_LABEL[k]}</option>)}
                </select>
              )}

              {i > 0 && slot.kind === 'logo' && (
                file ? (
                  <span className="flex min-w-0 flex-1 items-center gap-1 text-[11px] text-muted">
                    <span className="truncate">{file.name}</span>
                    <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => onClearFile(i)}>change</button>
                  </span>
                ) : (
                  <input
                    type="file"
                    accept={SLOT_FILE_ACCEPT}
                    className="min-w-0 flex-1 text-[11px]"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(i, f); }}
                  />
                )
              )}
              {/* Text zones are printed per player at order time, so they carry
                  no artwork at intake — by contract, not by omission. */}
              {i > 0 && slot.kind !== 'logo' && (
                <span className="flex-1 text-[11px] text-muted">printed per player at order time</span>
              )}

              {isTextFillKind(slot.kind) && (() => {
                const defaults = slotTypographyDefaults(opt?.area);
                const font = resolvePrizeFont(slot.font || defaults.font);
                const ink = resolvePrizeSlotInk(slot.ink || defaults.ink);
                return (
                  <div className="flex w-full flex-wrap items-center gap-1.5">
                    <select
                      className={`${selCls} min-w-0 flex-1`}
                      aria-label={`${opt?.label || slot.code} font`}
                      value={font}
                      onChange={(e) => onSlots(setSlotFont(slots, i, e.target.value))}
                    >
                      {PRIZE_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    <PrizeInkField
                      id={`prize-ink-${i}`}
                      aria-label={`${opt?.label || slot.code} font color`}
                      value={ink}
                      onChange={(next) => onSlots(setSlotInk(slots, i, next))}
                    />
                  </div>
                );
              })()}

              {i > 0 && (
                <button
                  type="button"
                  aria-label={`Remove ${opt?.label || slot.code}`}
                  className="rounded-[6px] p-1 text-muted transition-colors hover:bg-black/[0.06]"
                  onClick={() => { const out = removeSlot(slots, files, i); onSlots(out.slots, out.files); }}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
              {/* Every logo slot collects its own caption + font + color next
                  to the placement chip (Left Chest / Full Front / …). A T2
                  that mirrors Product 1 inherits the T1 primary caption unless
                  the operator unlinks it. Empty text requires nothing. */}
              {slot.kind === 'logo' && inheritPrimaryCaption && i === 0 && (
                <p data-slot-caption-on-zone={i} className="min-w-0 flex-1 text-[10px] text-subtle">Inherits Product 1 under-logo text</p>
              )}
              {slot.kind === 'logo' && !(inheritPrimaryCaption && i === 0) && (
                <div data-slot-caption-on-zone={i} className="flex w-full min-w-0 flex-wrap items-center gap-1.5">
                  <input
                    type="text"
                    maxLength={100}
                    placeholder="Text under this logo (optional) — e.g. EST. 2026"
                    className="h-7 min-w-0 flex-1 rounded-[6px] border border-black/[0.08] bg-surface px-2 text-[11px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15"
                    value={slot.decoration?.text || ''}
                    onChange={(e) => onSlots(setSlotText(slots, i, e.target.value, {
                      font: slot.decoration?.font || 'zingrustbase',
                      textColor: slot.decoration?.textColor || 'auto',
                    }))}
                  />
                  {slot.decoration?.text?.trim() ? (
                    <>
                      <AddtextFontSelect
                        aria-label={`${opt?.label || slot.code} caption font`}
                        value={slot.decoration.font || 'zingrustbase'}
                        onChange={(next) => onSlots(setSlotCaptionStyle(slots, i, { font: next }))}
                        fonts={[...ADDTEXT_PICKER_FONTS]}
                      />
                      <CaptionColorField
                        aria-label={`${opt?.label || slot.code} caption color`}
                        value={slot.decoration.textColor || 'auto'}
                        onChange={(next) => onSlots(setSlotCaptionStyle(slots, i, { textColor: next }))}
                      />
                    </>
                  ) : null}
                </div>
              )}
            </div>
            {hint && <p className="mt-0.5 text-[10px] text-subtle">{hint}</p>}
          </div>
        );
      })}

      {conflicts.map((p) => (
        <p key={p.join('-')} className="mt-1.5 flex items-start gap-1.5 text-[11px] text-red-600">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> {slotConflictMessage(registry, p)}
        </p>
      ))}

      {nothingLeft ? (
        <p className="mt-2 text-[11px] text-subtle">Every print zone this product supports is already in use.</p>
      ) : (
        <div className="mt-2 space-y-1.5">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-subtle">Add a print zone</p>
          {groups.standard.length > 0 && <div className="flex flex-wrap gap-1.5">{groups.standard.map(chip)}</div>}
          {groups.other.length > 0 && (
            <>
              <p className="text-[10.5px] text-subtle">Other</p>
              <div className="flex flex-wrap gap-1.5">{groups.other.map(chip)}</div>
            </>
          )}
          {/* ONE operator choice, TWO slots — slot codes are unique per tier, so
              "stacked name and number" is the registry's declared BN+BC pair. */}
          {pair && (
            <div className="pt-0.5">
              <button
                type="button"
                onClick={() => onSlots(addStackPair(registry, opts, slots))}
                className="rounded-full border border-black/[0.08] bg-surface px-2.5 py-1 text-[11px] transition-colors hover:bg-bg"
              >
                + Name + number (stacked)
              </button>
              <p className="mt-1 text-[10px] text-subtle">
                Adds {pair.ordered[0].label} ({pair.ordered[0].code}) above {pair.ordered[1].label} ({pair.ordered[1].code})
                {' '}— two separate prints in one area: each player&rsquo;s {pair.ordered[0].kind === 'name' ? 'name' : 'number'} on top,
                their {pair.ordered[1].kind === 'name' ? 'name' : 'number'} underneath.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
